import React, { useState, useEffect } from "react";
import { IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

interface FontSizeControlProps {
  onFontSizeChange: (fontSize: number) => void;
  disabled?: boolean;
}

const FontSizeControl: React.FC<FontSizeControlProps> = ({
  onFontSizeChange,
  disabled = false,
}) => {
  const [fontSize, setFontSize] = useState(100); // Percentage, default 100%

  // Load font size from localStorage on mount
  useEffect(() => {
    const savedFontSize = localStorage.getItem("editorFontSize");
    if (savedFontSize) {
      const size = parseInt(savedFontSize, 10);
      setFontSize(size);
      onFontSizeChange(size);
    } else {
      // Set default to 115% (15% larger than current)
      const defaultSize = 115;
      setFontSize(defaultSize);
      onFontSizeChange(defaultSize);
      localStorage.setItem("editorFontSize", defaultSize.toString());
    }
  }, []); // Empty dependency array - only run on mount

  const handleIncrease = () => {
    if (disabled) return;
    const newSize = Math.min(fontSize + 10, 200); // Max 200%
    setFontSize(newSize);
    onFontSizeChange(newSize);
    localStorage.setItem("editorFontSize", newSize.toString());
  };

  const handleDecrease = () => {
    if (disabled) return;
    const newSize = Math.max(fontSize - 10, 60); // Min 60%
    setFontSize(newSize);
    onFontSizeChange(newSize);
    localStorage.setItem("editorFontSize", newSize.toString());
  };

  if (disabled) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "1px",
        backgroundColor: "transparent",
        borderRadius: "4px",
        padding: "0px",
        opacity: 0.7,
        transition: "opacity 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = "1";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = "0.7";
      }}
    >
      <Tooltip title="Decrease font size" placement="top" arrow>
        <IconButton
          size="small"
          onClick={handleDecrease}
          disabled={fontSize <= 60}
          style={{
            width: "18px",
            height: "18px",
            color: "#999",
            backgroundColor: "transparent",
            minWidth: "18px",
            padding: "2px",
          }}
          sx={{
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.05)",
              color: "#666",
            },
            "&:disabled": {
              color: "#ddd",
            },
          }}
        >
          <RemoveIcon style={{ fontSize: "12px" }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Increase font size" placement="top" arrow>
        <IconButton
          size="small"
          onClick={handleIncrease}
          disabled={fontSize >= 200}
          style={{
            width: "18px",
            height: "18px",
            color: "#999",
            backgroundColor: "transparent",
            minWidth: "18px",
            padding: "2px",
          }}
          sx={{
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.05)",
              color: "#666",
            },
            "&:disabled": {
              color: "#ddd",
            },
          }}
        >
          <AddIcon style={{ fontSize: "12px" }} />
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default FontSizeControl;
